import { motion } from 'framer-motion';
import { Calendar, Tag, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { ROUTE_PATHS } from '@/lib/index';
import { springPresets } from '@/lib/motion';

const NEWS_ITEMS = [
  {
    id: 1,
    date: '2026-03-15',
    category: '新航点',
    title: '坦桑尼亚航空宣布新增塞舌尔（SEZ）航线',
    excerpt: 'Air Tanzania 宣布将开通达累斯萨拉姆至塞舌尔马埃岛（SEZ）新航线，进一步丰富印度洋度假目的地覆盖。',
    image: '/images/zanzibar.jpg',
    tag: '🆕 新航点',
  },
  {
    id: 2,
    date: '2026-02-20',
    category: '公告',
    title: '2026年春节特别促销：广州出发非洲特价机票',
    excerpt: '坦桑尼亚航空推出广州直飞达累斯萨拉姆及桑给巴尔春节特别机票，经济舱起价优惠，欢迎联系中国代表处咨询。',
    image: '/images/serengeti.jpg',
    tag: '促销',
  },
  {
    id: 3,
    date: '2026-01-10',
    category: '机队',
    title: '第二架波音737 MAX 9交付，机队扩至16架',
    excerpt: '坦桑尼亚航空正式接收第二架波音737 MAX 9飞机，进一步扩展区域和国际航线覆盖能力，总运营机队达到16架。',
    image: '/images/max9.jpg',
    tag: '机队更新',
  },
  {
    id: 4,
    date: '2025-12-01',
    category: '服务',
    title: 'Twiga Miles 积分计划完成升级，新增合作伙伴',
    excerpt: '坦桑尼亚航空更新 Twiga Miles 常旅客计划，新增酒店、租车等合作伙伴，里程兑换选项大幅扩展。',
    image: '/images/experience.jpg',
    tag: '服务升级',
  },
  {
    id: 5,
    date: '2025-11-15',
    category: '安全',
    title: '坦桑尼亚航空通过IOSA 2025年度复审认证',
    excerpt: 'Air Tanzania 顺利通过 IATA 运营安全审计（IOSA）2025年度复审，持续保持国际安全认证资质。',
    image: '/images/pilot.jpg',
    tag: '安全认证',
  },
  {
    id: 6,
    date: '2025-10-08',
    category: '货运',
    title: 'Air Tanzania Cargo 广州至达累斯萨拉姆货运航班增班',
    excerpt: '应中非贸易增长需求，Air Tanzania Cargo 广州出发货运班次由每周2班增至每周3班，满足更大货运需求。',
    image: '/images/cargo-freighter.jpg',
    tag: '货运扩张',
  },
];

export default function News() {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative h-64 overflow-hidden">
        <img src="/images/serengeti.jpg" alt="新闻公告" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/50 to-secondary/85" />
        <div className="absolute inset-0 flex items-center pt-20">
          <div className="max-w-7xl mx-auto px-4">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">新闻与公告</h1>
            <p className="text-white/70">最新动态 · 促销活动 · 服务更新</p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Featured */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="mb-10"
          >
            <Link
              to={ROUTE_PATHS.ROUTES}
              className="group grid grid-cols-1 lg:grid-cols-2 rounded-2xl overflow-hidden border border-border bg-card hover:shadow-xl transition-all"
            >
              <div className="relative h-60 lg:h-auto">
                <img
                  src={NEWS_ITEMS[0].image}
                  alt={NEWS_ITEMS[0].title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4 bg-accent text-accent-foreground text-xs font-semibold px-3 py-1.5 rounded-full">
                  {NEWS_ITEMS[0].tag}
                </div>
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">{NEWS_ITEMS[0].category}</span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar size={11} />
                    {NEWS_ITEMS[0].date}
                  </span>
                </div>
                <h2 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors">
                  {NEWS_ITEMS[0].title}
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">{NEWS_ITEMS[0].excerpt}</p>
                <div className="flex items-center gap-2 text-primary font-medium text-sm group-hover:gap-3 transition-all">
                  阅读详情 <ArrowRight size={14} />
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {NEWS_ITEMS.slice(1).map((item, i) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springPresets.gentle, delay: i * 0.1 }}
              >
                <Link
                  to={ROUTE_PATHS.NEWS}
                  className="group block rounded-2xl overflow-hidden border border-border bg-card hover:shadow-lg transition-all h-full"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="bg-secondary/80 text-white text-xs px-2 py-1 rounded-full">
                        {item.tag}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs flex items-center gap-1 text-primary">
                        <Tag size={10} />
                        {item.category}
                      </span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar size={10} />
                        {item.date}
                      </span>
                    </div>
                    <h3 className="font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2">{item.title}</h3>
                    <p className="text-muted-foreground text-sm line-clamp-3">{item.excerpt}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
